"use client";

import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { MIN_TAP_TARGET, RADIUS } from "src/themes/tokens";

/**
 * Counter name shared by every StepHeading inside one `counterReset` scope.
 * A CSS counter rather than a prop, so a step that is not asked (the frame,
 * for paper) drops out of the numbering by not rendering.
 */
export const STEP_COUNTER = "canvas-step";

/**
 * Desktop density for the steps and cards inside it. The editors' option
 * column sits beside the preview; at full size its cards ran two screens
 * down. Dense keeps the same look — numbered steps, brand border, tint and
 * tick — at a smaller size. Phones keep the large cards.
 */
export const OptionDensityContext = React.createContext(false);

/**
 * A numbered, bold heading for one decision on the page.
 *
 * The page asks up to five things. Small grey labels over small grey toggles
 * made them read as one block of fine print; a number and a heavy heading say
 * "this is a question, answer it" before anything is read.
 */
export function StepHeading({
  id,
  children,
  aside,
}: {
  id?: string;
  children: React.ReactNode;
  aside?: React.ReactNode;
}) {
  const dense = React.useContext(OptionDensityContext);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: dense ? 1 : 1.5, flexWrap: "wrap" }}>
      <Typography
        id={id}
        component="h3"
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: dense ? 1 : 1.25,
          fontSize: dense ? 15 : 18,
          fontWeight: 800,
          lineHeight: 1.3,
          color: theme.tokens.ink,
          "&::before": {
            counterIncrement: STEP_COUNTER,
            content: `counter(${STEP_COUNTER})`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: dense ? 22 : 28,
            height: dense ? 22 : 28,
            borderRadius: "50%",
            bgcolor: theme.tokens.brand,
            color: "#fff",
            fontSize: dense ? 12 : 15,
            fontWeight: 800,
          },
        })}
      >
        {children}
      </Typography>
      {aside}
    </Box>
  );
}

export interface OptionCardProps {
  selected: boolean;
  onClick: () => void;
  title: React.ReactNode;
  /** One plain line saying what this choice means for the customer. */
  subtitle?: React.ReactNode;
  /** Right-aligned or trailing detail — usually the price. */
  price?: React.ReactNode;
  /** Leading visual, e.g. an orientation glyph. */
  icon?: React.ReactNode;
  /** The icon above a centred title, for narrow cards four across. */
  stacked?: boolean;
  disabled?: boolean;
}

/**
 * One answer to a step, as a large tappable card.
 *
 * Built for the customer who finds a 13px grey toggle hard to read or hit:
 * the label is bold and 17px, the target is at least 64px tall, and the
 * chosen card is marked three ways at once — a thick brand border, a tinted
 * ground and a tick — so the choice is obvious without relying on colour.
 */
export default function OptionCard({
  selected,
  onClick,
  title,
  subtitle,
  price,
  icon,
  stacked = false,
  disabled,
}: OptionCardProps) {
  const dense = React.useContext(OptionDensityContext);
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onClick}
      focusRipple
      sx={(theme) => ({
        position: "relative",
        width: "100%",
        minHeight: Math.max(dense ? 48 : 64, MIN_TAP_TARGET),
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        alignItems: "center",
        justifyContent: stacked ? "center" : "flex-start",
        gap: stacked ? 0.75 : dense ? 1.25 : 1.5,
        textAlign: stacked ? "center" : "left",
        px: dense ? 1.5 : 2,
        py: dense ? 1 : 1.5,
        pr: stacked ? (dense ? 1.5 : 2) : dense ? 4 : 5,
        borderRadius: `${RADIUS.card}px`,
        border: "2px solid",
        borderColor: selected ? theme.tokens.brand : theme.palette.grey[300],
        bgcolor: selected ? theme.tokens.brandSoft : theme.tokens.surface,
        boxShadow: selected ? `0 0 0 1px ${theme.tokens.brand}` : "none",
        transition: "border-color 120ms, background-color 120ms",
        "&:hover": {
          borderColor: theme.tokens.brand,
        },
        "&.Mui-focusVisible": {
          outline: `3px solid ${theme.tokens.brand}`,
          outlineOffset: 2,
        },
        "&.Mui-disabled": { opacity: 0.45 },
      })}
    >
      {icon}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="span"
          sx={(theme) => ({
            display: "block",
            fontSize: dense ? 14.5 : 17,
            fontWeight: 800,
            lineHeight: 1.3,
            color: theme.tokens.ink,
          })}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            component="span"
            sx={(theme) => ({
              display: "block",
              mt: 0.25,
              fontSize: dense ? 12.5 : 14,
              lineHeight: 1.4,
              color: theme.tokens.ink2,
            })}
          >
            {subtitle}
          </Typography>
        )}
        {price && (
          <Typography
            component="span"
            sx={(theme) => ({
              display: "block",
              mt: dense ? 0.25 : 0.5,
              fontSize: dense ? 13.5 : 15,
              fontWeight: 700,
              whiteSpace: "nowrap",
              color: theme.tokens.brand,
            })}
          >
            {price}
          </Typography>
        )}
      </Box>
      {selected && (
        <CheckCircleIcon
          aria-hidden
          sx={(theme) => ({
            position: "absolute",
            top: dense ? 6 : 10,
            right: dense ? 6 : 10,
            fontSize: dense ? 18 : 24,
            color: theme.tokens.brand,
          })}
        />
      )}
    </ButtonBase>
  );
}
